'use client';

import React, { useMemo, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getETDate } from '@/lib/marketTime';
import { MARKET_HOLIDAYS, MARKET_EARLY_CLOSES } from '@/lib/marketHolidays';

type HolidayEntry = { date: string; name: string; halfDay?: boolean };

/** Single source of truth: the canonical full-closure table plus the
 *  half-day early-close map (both shared with the server). Previously this
 *  component recomputed the whole calendar (incl. Easter) client-side, which
 *  both duplicated the tables and got the date strings off-by-one for
 *  positive-UTC-offset users (local-zone Date + toISOString). */
function buildHolidayList(): HolidayEntry[] {
  const full: HolidayEntry[] = Object.entries(MARKET_HOLIDAYS).map(([date, name]) => ({ date, name }));
  const half: HolidayEntry[] = Object.keys(MARKET_EARLY_CLOSES).map((date) => ({
    date,
    name: 'Early Close (1pm)',
    halfDay: true,
  }));
  return [...full, ...half].sort((a, b) => a.date.localeCompare(b.date));
}

export default function MarketHolidaysStrip() {
  const todayStr = getETDate(new Date());
  const scrollRef = useRef<HTMLDivElement>(null);

  const holidays = useMemo(() => {
    // Show from today's ET date onwards.
    return buildHolidayList().filter((h) => h.date >= todayStr).slice(0, 12);
  }, [todayStr]);

  // Auto-scroll to first upcoming
  useEffect(() => {
    if (scrollRef.current) {
      const first = scrollRef.current.querySelector('[data-upcoming="true"]');
      if (first) first.scrollIntoView({ inline: 'start', block: 'nearest' });
    }
  }, [holidays]);

  if (holidays.length === 0) return null;

  return (
    <div className="bg-card-bg border border-default rounded-xl p-3 mb-4">
      <div className="flex items-center gap-2 mb-2 px-1">
        <span className="text-xs font-semibold text-fg-muted uppercase tracking-wide">Upcoming Market Holidays</span>
      </div>
      <div ref={scrollRef} className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
        {holidays.map((h) => {
          // A holiday is a fixed ET calendar date, not an instant — render its
          // labels from UTC noon so the displayed weekday/day/month is the
          // date itself regardless of the viewer's timezone.
          const d = new Date(h.date + 'T12:00:00Z');
          const isToday = h.date === todayStr;
          const isPast = h.date < todayStr;
          const monthStr = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
          const dayNum = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
          const dayName = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

          return (
            <div
              key={h.date + h.name}
              data-upcoming={!isPast ? 'true' : undefined}
              className={cn(
                'flex flex-col items-center min-w-[80px] px-3 py-2 rounded-lg flex-shrink-0 border transition-colors',
                isToday ? 'bg-amber-500/10 border-amber-500/30' :
                h.halfDay ? 'bg-sky-500/5 border-sky-500/20' :
                'bg-card-hover-bg border-default',
                isPast && 'opacity-40',
              )}
            >
              <span className="text-[10px] text-fg-subtle uppercase">{dayName}</span>
              <span className={cn('text-lg font-bold', isToday ? 'text-amber-400' : 'text-white')}>{dayNum}</span>
              <span className="text-[10px] text-fg-subtle">{monthStr}</span>
              <span className={cn(
                'text-[9px] font-medium mt-1 text-center leading-tight',
                h.halfDay ? 'text-sky-400' : isToday ? 'text-amber-300' : 'text-fg-muted',
              )}>
                {h.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
